// Session ANV: step 4 of the Director's own workflow -- "accept it as real work" -- and the join
// between two systems that had no relationship of any kind before this.
//
// WHAT WAS WRONG. `CallLog` (Log a call) and `Job` shared no column, no FK and no action: logging a
// call and raising the work it was about were two separate acts of typing, and a completed job
// carried no record of the call that started it. This modal is the one control that turns the first
// into the second, and the link it passes on is what makes the trail exist afterwards.
//
// IT IS A HUMAN DECISION AND NOTHING INFERS IT. The call's own `outcome` field is deliberately NOT
// consulted, not even as a default: "follow-up needed" is a note about a conversation, not a
// decision to send an engineer to a building, and a call marked "resolved" is very often exactly the
// one that needs a repair planning in. So the office is asked, in plain words, with the call in
// front of them.
//
// WHY IT OFFERS TWO ROUTES AND NOT ONE FORM. A call-out and the four undated types are genuinely
// different pieces of work, raised by different routes, and this session did not force them into one
// screen:
//   * A CALL-OUT is a fault reported now. It needs the site, then the lift on that site, then the
//     fault, in that order (you cannot say which lift is faulty until you know which building), plus
//     the reported time, the out-of-hours basis, travel, the entrapment toggle and an agreed price.
//     The Log call-out form on the Call-outs page already walks exactly that sequence, so this hands
//     off to it with the call carried along rather than reproducing it.
//   * PLANNED REPAIR / FULL CONDITION REPORT / SITE INSPECTION / TECH VISIT are work with no date,
//     no fault-at-this-moment and no rate to settle. RaiseWorkModal already branches correctly
//     between those four; it opens here with the caller's own lifts in front of it.
import type { CallLogDto } from "../../lib/api";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";

const CARD = "mb-2 flex w-full cursor-pointer items-start gap-3 rounded-ele border border-ele-purple-accent p-3 text-left transition hover:border-ele-green";

// Session ARE (D82): this modal asks the same "is this real work?" question of an Enquiry
// (src/routes/office/EnquiryDetailPage.tsx) as it already asks of a logged call, and an Enquiry is
// not a CallLogDto. Widened structurally, additively, to the exact fields this file actually reads
// off `call` (confirmed by reading the whole component, not assumed): a logged call and an enquiry
// each satisfy this by construction, so neither existing host (CallLogPage.tsx, CustomerCallLog.tsx)
// changes behaviour.
export type RaiseWorkSource = Pick<CallLogDto, "id" | "customerId" | "customerName" | "callerName" | "callerPhone" | "topic">;

// Who rang, as the office would say it out loud. A matched customer by name; an unmatched caller by
// whatever was captured; neither, only when the row genuinely holds neither (a webhook row with no
// number), and it says so rather than printing an empty string.
export function callerLine(call: RaiseWorkSource): string {
  if (call.customerName) return call.customerName;
  const parts = [call.callerName, call.callerPhone].filter((v) => v && v.trim().length > 0);
  return parts.length > 0 ? parts.join(" · ") : "Unknown caller";
}

export function AcceptCallAsWorkModal({
  call,
  onClose,
  onCallout,
  onPlannedWork,
  onQuote,
}: {
  call: RaiseWorkSource;
  onClose: () => void;
  // Hand off to the Log call-out form, carrying this call.
  onCallout: () => void;
  // Open the four undated raise types, narrowed to this caller's lifts.
  onPlannedWork: () => void;
  // Session AQS, W-1: a caller asking about a NEW maintenance agreement rather than a fault or
  // planned repair -- hands off to the ordinary office quote calculator, prefilled with whatever
  // this call already knows (customer only; a call log holds no site or lift), for the office to
  // price like any other quote.
  onQuote: () => void;
}) {
  return (
    <ModalShell ariaLabel="Raise work from this call" onClose={onClose} width="max-w-[560px]" panelClassName="p-6 max-h-[85vh] overflow-y-auto">
      <div className="mb-3">
        <h2 className="font-heading text-xl text-ele-strong">Raise work from this call</h2>
        <p className="text-sm text-ele-body">
          Whether a call is real work is your decision, not the platform&apos;s. Nothing here is worked out from how the call was marked.
        </p>
      </div>

      <div className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm">
        <p className="text-ele-strong">{callerLine(call)}</p>
        {call.topic && <p className="mt-1 text-ele-body">{call.topic}</p>}
      </div>

      {/* Session ARM (D88): a site inspection is the one piece of work an unknown caller can have
          raised as they are, because RaiseWorkModal offers "This caller" as its target and the server
          makes their sales lead then. Every other route still needs a customer and a lift. */}
      {!call.customerId && (
        <p className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-body">
          This caller is not matched to a customer on the register, so nothing can be filled in for you. For most work you will choose the customer
          and the lift, or create them, as part of raising it. A site inspection can be raised for the caller as they are: choose &quot;Work to plan
          in&quot;, then Site inspection, and a sales lead is made for them from this call.
        </p>
      )}

      <p className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">What kind of work is it?</p>

      <button type="button" onClick={onCallout} className={CARD}>
        <span className="text-sm text-ele-strong">
          A fault to attend now: raise a call-out
          <span className="block text-ele-body">
            Opens the Log call-out form with this call carried across. You choose the site, then the lift on that site, then describe the fault, and the
            call-out is what the engineer is dispatched to.
          </span>
        </span>
      </button>

      <button type="button" onClick={onPlannedWork} className={CARD}>
        <span className="text-sm text-ele-strong">
          Work to plan in: repair, condition report, inspection or tech visit
          <span className="block text-ele-body">
            Opens the Raise work form{call.customerName ? `, showing ${call.customerName}'s lifts` : ""}. It lands on the Needs scheduling panel with no
            date, for you to plan in.
          </span>
        </span>
      </button>

      <button type="button" onClick={onQuote} className={CARD}>
        <span className="text-sm text-ele-strong">
          A new agreement to price: start a quote
          <span className="block text-ele-body">
            Opens the ordinary quote calculator{call.customerName ? `, prefilled for ${call.customerName}` : ""}, for you to add the site and lifts and
            price it like any other quote.
          </span>
        </span>
      </button>

      <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
        <button
          type="button"
          onClick={onClose}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm ${MODAL_ACTION_BTN}`}
        >
          Not work: close
        </button>
      </div>
    </ModalShell>
  );
}
